// The recap's one neutral voice. The backend stores facts and a phrase_index;
// this file is the only place wording lives (spec §3).
export const PHRASES = {
  swing: ['{t} changed the playoff picture more than anyone this week.', 'No result mattered more than {tp} result.', 'The week’s biggest shift belonged to {t}.'],
  rise: ['{t} moved up {n} places on how they actually score, not just their record.', 'Nobody gained more ground than {t} this week.', '{t} are scoring like a top team.'],
  upset: ['{w} beat a team that had been scoring {gap} more a week.', 'On paper {l} were the pick. {w} didn’t read it.', 'The underdog came through: {w} over {l}.'],
  streak: ['{t} have now won {nw} straight.', '{N} in a row for {t}, and counting.', 'The streak is at {n} for {t}.'],
  skid: ['{t} have dropped {nw} in a row.', 'The slide continues for {t}: {n} straight losses.', '{N} straight losses for {t}.'],
  snapped: ['{tp} {n}-game run is over.', 'The streak ends at {n} for {t}.', '{t} finally lost after {n} straight wins.'],
  clinch: ['{t} have clinched a playoff spot.', 'It’s official: {t} are in.', '{t} locked up their place in the playoffs.'],
  out: ['{t} have been eliminated from playoff contention.', 'The season is over for {tp} playoff hopes.', '{t} are out of the running.'],
  big: ['{t} put up {s}, one of the best weeks the league has seen.', 'A {s}-point week for {t}.', '{t} scored {s} and set the bar for everyone.'],
  nailbiter: ['{w} got past {l} by {m}. It came down to the last lineup slot.', 'Decided by {m}: {w} over {l}.', 'A coin-flip game went to {w}, by {m}.'],
  blowout: ['{w} beat {l} by {m}.', 'A {m}-point win for {w}.', '{w} were never in danger against {l}.'],
  heartbreak: ['{t} scored {s}, above the league median, and still lost.', '{t} deserved better: {s} points and a loss.', 'A tough one for {t}: {s} points wasn’t enough.'],
  champion: ['{w} are champions, {s} to {os} against {l}.', 'The title belongs to {w}.', '{w} finish the season on top, with {l} runners-up.'],
  playoff_upset: ['The #{ws} seed {w} knocked out #{ls} {l}.', 'Seeding didn’t hold: {w} beat {l} by {m}.', '{w} are through, and {l} are out.'],
  advance: ['{w} beat {l} by {m} and move on.', '{w} are one of the last {nx}.', 'No surprise: {w} advance past {l}.'],
  title_swing: ['{tp} title odds moved {d} points, to {ta}.', 'Nobody’s title chances moved more than {tp}.', '{t} now have a {ta} chance at the title.'],
};
const ALIAS = { upset_nailbiter: 'upset', quiet: 'big' };
const WORDS = ['zero', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine', 'ten', 'eleven', 'twelve'];

export const possessive = (name) => (name.endsWith('s') ? `${name}’` : `${name}’s`);
export const ordinal = (n) => { const m = n % 100; const s = m >= 11 && m <= 13 ? 'th' : ['th', 'st', 'nd', 'rd'][n % 10] || 'th'; return `${n}${s}`; };
export const words = (n) => (n >= 0 && n < WORDS.length ? WORDS[n] : String(n));
const f1 = (v) => Number(v).toFixed(1);
const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

function vars(story, names) {
  const { type, teams, facts } = story;
  const t = names[teams[0]] ?? teams[0];
  const w = t, l = names[teams[1]] ?? teams[1];
  const lengthValue = facts.length != null ? facts.length : type === 'rise' ? facts.from - facts.to : null;
  const n = lengthValue != null ? String(lengthValue) : '';
  const nw = lengthValue != null ? words(lengthValue) : '';
  return {
    t, tp: possessive(t), w, l, n, nw, N: cap(nw), gap: f1(facts.gap ?? 0), m: f1(facts.margin ?? 0),
    s: f1(facts.points ?? 0),
    os: f1(facts.opp_points ?? 0), ws: String(facts.winner_seed ?? ''), ls: String(facts.loser_seed ?? ''),
    nx: words(facts.teams_alive_next ?? 0), d: String(Math.abs(Math.round(facts.delta ?? 0))), ta: `${Math.round(facts.after ?? 0)}%`,
  };
}

export function storyHeadline(story, names) {
  const { type, teams, facts } = story;
  const t = names[teams[0]] ?? teams[0], o = names[teams[1]] ?? teams[1];
  switch (type) {
    case 'swing': return `${t} ${facts.delta > 0 ? 'jump' : 'slip'} to ${Math.round(facts.after)}% playoff odds`;
    case 'rise': return `${t} climb to ${ordinal(facts.to)} in the power ranking`;
    case 'upset': return `${t} upset ${o}`;
    case 'upset_nailbiter': return `${t} upset ${o} by ${f1(facts.margin)}`;
    case 'streak': return `${t} make it ${words(facts.length)} straight`;
    case 'skid': return `${t} drop ${words(facts.length)} straight`;
    case 'snapped': return `${possessive(t)} streak ends at ${facts.length}`;
    case 'out': return `${t} are eliminated`;
    case 'clinch': {
      const who = teams.length > 2 ? `${cap(words(teams.length))} teams` : teams.map(id => names[id] ?? id).join(' and ');
      return `${who} clinch${teams.length > 1 ? ' playoff spots' : ' a playoff spot'}`;
    }
    case 'big': return `${t} post ${f1(facts.points)}`;
    case 'blowout': return `${t} rout ${o}`;
    case 'nailbiter': return `${t} edge ${o} by ${f1(facts.margin)}`;
    case 'heartbreak': return `Heartbreak for ${t}`;
    case 'quiet': return `${t} lead the week with ${f1(facts.points)}`;
    case 'champion': return `${t} win the championship`;
    case 'playoff_upset': return `#${facts.winner_seed} ${t} knock out #${facts.loser_seed} ${o}`;
    case 'advance': return `${t} advance past ${o}`;
    case 'title_swing': return `${t} ${facts.delta > 0 ? 'rise' : 'fall'} to ${Math.round(facts.after)}% title odds`;
    default: return t;
  }
}

export function storyBody(story, names) {
  const list = PHRASES[ALIAS[story.type] ?? story.type] ?? PHRASES.big;
  const v = story.type === 'clinch' ? { ...vars(story, names), t: story.teams.map(id => names[id] ?? id).join(' and ') } : vars(story, names);
  return list[(story.phrase_index ?? 0) % list.length].replace(/\{(\w+)\}/g, (m, k) => (v[k] != null ? v[k] : m));
}
